import { BottomSheetModal } from '@gorhom/bottom-sheet/src';
import welcome from 'assets/images/welcome/welcome.png';
import {
  NativeStackHeaderProps,
  Stack,
  useGlobalSearchParams,
  useRouter,
} from 'expo-router';
import varoBankDisclosureUrl from 'features/auth/welcome/constant/VaroBankDisclosureUrl';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ImageBackground,
  ImageSourcePropType,
  ScrollView,
  StyleSheet,
  // eslint-disable-next-line no-restricted-imports
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import ExpressiveXLText from 'shared/components/vxl/typography/expressive/ExpressiveXLText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkBaseText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkBaseText';
import HyperlinkXsText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkXSText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import VaroLogo from 'shared/components/vxl/VaroLogo/VaroLogo';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

const WelcomeScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('auth', {
    keyPrefix: 'welcome',
  });
  const params = useGlobalSearchParams();
  const { top, bottom } = useSafeAreaInsets();

  const logDisclosureClickedAnalytics = usePressableAnalytics({
    analyticsId: 'disclosureClicked',
  });

  const bottomSheetRef = useRef<BottomSheetModal>(null);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

  const openBottomSheet = useCallback(() => {
    logDisclosureClickedAnalytics();
    bottomSheetRef.current?.present();
  }, [logDisclosureClickedAnalytics]);

  const dismissBottomSheet = useCallback(() => {
    bottomSheetRef.current?.dismiss();
  }, []);

  const onDisclosureHyperlinkClicked = useCallback(() => {
    dismissBottomSheet();
    router.push({
      pathname: '/welcome/disclosure',
      params: { pdfUrl: varoBankDisclosureUrl },
    });
  }, [dismissBottomSheet, router]);

  const onSignupClicked = () => router.replace({ pathname: '/signup', params });
  const onLoginClicked = () => router.replace('/(public)/login');

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: false,
          headerBackVisible: false,
          headerShadowVisible: false,
          headerTitle: '',
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <View style={styles.container}>
        <ScrollView
          style={styles.scrollContainer}
          contentContainerStyle={{ paddingTop: top, paddingBottom: bottom }}
        >
          <View testID="varoLogo" style={styles.logoContainer}>
            <VaroLogo width={64} height={24} />
          </View>
          <ImageBackground
            testID="welcomeImage"
            source={welcome as ImageSourcePropType}
            resizeMode="contain"
            style={styles.welcomeImage}
          />
          <ExpressiveXLText numberOfLines={0} style={styles.titleContainer}>
            {translatedText('title')}
          </ExpressiveXLText>
          <BodyBaseText numberOfLines={0} style={styles.subTitleContainer}>
            {translatedText('subtitle')}
          </BodyBaseText>

          <View style={styles.signUpButton} testID="welcome-sign-up-button">
            <Button
              label={translatedText('buttons.signUp')}
              onPress={onSignupClicked}
              analyticsId="welcomeSignupClicked"
              size="LARGE"
              type="PRIMARY"
              width="FULL"
            />
          </View>
          <View style={styles.loginButton} testID="welcome-log-in-button">
            <Button
              label={translatedText('buttons.logIn')}
              onPress={onLoginClicked}
              analyticsId="welcomeLoginClicked"
              size="LARGE"
              type="SECONDARY"
              width="FULL"
            />
          </View>

          <View
            style={styles.disclaimerContainer}
            pointerEvents={isBottomSheetOpen ? 'none' : 'auto'}
          >
            <BodyXSText numberOfLines={0}>
              {translatedText('disclosure')}
            </BodyXSText>
            <HyperlinkXsText numberOfLines={0} onPress={openBottomSheet}>
              {translatedText('disclosureLink')}
            </HyperlinkXsText>
          </View>
        </ScrollView>
      </View>

      {/* Disclosure bottom sheet */}
      <BottomSheet
        ref={bottomSheetRef}
        enableDynamicSizing={false}
        snapPoints={['60%']}
        onChange={(index) => {
          setIsBottomSheetOpen(index >= 0);
        }}
      >
        <View style={styles.bottomSheet}>
          <TitleMdText numberOfLines={0}>
            {translatedText('disclosureLink')}
          </TitleMdText>

          <View style={styles.bottomSheetDisclosure}>
            <BodyBaseText key="disclosureOne">
              <Text>{translatedText('disclosureText.one')}</Text>
              <HyperlinkBaseText onPress={onDisclosureHyperlinkClicked}>
                {translatedText('disclosureText.hyperlink')}
              </HyperlinkBaseText>
            </BodyBaseText>

            <BodyBaseText key="disclosureTwo">
              {translatedText('disclosureText.two')}
            </BodyBaseText>
            <BodyBaseText key="disclosureThree">
              {translatedText('disclosureText.three')}
            </BodyBaseText>
          </View>

          <View style={styles.doneButton}>
            <Button
              label={translatedText('buttons.done')}
              onPress={dismissBottomSheet}
              size="LARGE"
              type="PRIMARY"
              width="FULL"
            />
          </View>
        </View>
      </BottomSheet>
    </View>
  );
};

const styles = StyleSheet.create({
  bottomSheet: {
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  bottomSheetDisclosure: {
    paddingBottom: theme.spacing.m,
    paddingTop: theme.spacing.s,
  },
  container: {
    backgroundColor: theme.color.expressive.orchid,
    flex: 1,
    flexDirection: 'column',
    flexGrow: 1,
  },
  disclaimerContainer: {
    alignSelf: 'center',
    flexDirection: 'row',
    paddingVertical: theme.spacing.m,
  },
  doneButton: {
    paddingVertical: theme.spacing.m,
  },
  loginButton: {
    paddingTop: theme.spacing.m,
  },
  logoContainer: {
    alignItems: 'flex-start',
    paddingTop: theme.spacing.m,
  },
  scrollContainer: {
    flex: 1,
    padding: theme.spacing.m,
  },
  signUpButton: {
    paddingTop: theme.spacing.m,
  },
  subTitleContainer: {
    color: theme.color.expressive.eggplant,
    marginVertical: theme.spacing.m,
    textAlign: 'center',
  },
  titleContainer: {
    color: theme.color.expressive.eggplant,
    marginTop: theme.spacing.xxl,
    textAlign: 'center',
  },
  welcomeImage: {
    alignSelf: 'center',
    minHeight: 316,
    minWidth: 375,
  },
});

export default WelcomeScreen;
